import React, { useEffect, useState, useCallback, useRef } from "react";
import { Grid, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, TextField, InputAdornment } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { formatData } from "utilities/datatable";
import axios from "axios";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  button: {
    margin: theme.spacing(1),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    marginBottom: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
  },
  searchContainer: {
    marginBottom: theme.spacing(2),
  },
  searchField: {
    width: "100%",
  },
}));

function ContactsFeedback() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const isInitialMount = useRef(true);

  const fetchContacts = useCallback(() => {
    setLoading(true);
    const api = "https://se-azgb-tp-dvfun04.azurewebsites.net/api/SearchRecordAsync";
    const payload = {
      recordType: "contacts",
      filters: [],
      fieldAndOperator: false,
      idfield: "contactid"
    };

    axios.post(api, payload, {
      headers: {
        "Authorization": "Bearer " + localStorage.getItem("token"),
        "Content-Type": "application/json"
      }
    })
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          // The SearchRecordAsync returns an array of DataverseRecord objects
          // Wrap it in the format that formatData expects: { data: [...] }
          const wrappedResponse = { data: res.data };
          const formattedContacts = formatData(wrappedResponse);
          setContacts(formattedContacts);
        } else {
          setContacts([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log("Error fetching contacts:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: "Failed to load contacts",
            code: "",
            display: "show",
            severity: "error",
          },
        });
        setLoading(false);
      });
  }, [dispatch]);

  const searchContacts = useCallback((searchValue) => {
    if (!searchValue || searchValue.trim() === "") {
      // If search is empty, fetch all contacts
      fetchContacts();
      return;
    }

    setIsSearching(true);
    const api = "https://se-azgb-tp-dvfun04.azurewebsites.net/api/SearchRecordAsync";
    const payload = {
      recordType: "contacts",
      filters: [
        {
          field: "firstname",
          searchvalue: searchValue,
          type: "string",
          operator: "eq"
        },
        {
          field: "lastname",
          searchvalue: searchValue,
          type: "string",
          operator: "eq"
        },
        {
          field: "ctpt_membershipnumber",
          searchvalue: searchValue,
          type: "string",
          operator: "eq"
        }
      ],
      fieldAndOperator: false, // Use OR operator to search in firstname, lastname, or membership number
      idfield: "contactid"
    };

    axios.post(api, payload, {
      headers: {
        "Authorization": "Bearer " + localStorage.getItem("token"),
        "Content-Type": "application/json"
      }
    })
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          // The SearchRecordAsync returns an array of DataverseRecord objects
          // Wrap it in the format that formatData expects: { data: [...] }
          const wrappedResponse = { data: res.data };
          const formattedContacts = formatData(wrappedResponse);
          setContacts(formattedContacts);
        } else {
          setContacts([]);
        }
        setIsSearching(false);
      })
      .catch((error) => {
        console.log("Error searching contacts:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: "Failed to search contacts",
            code: "",
            display: "show",
            severity: "error",
          },
        });
        setIsSearching(false);
      });
  }, [dispatch, fetchContacts]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Debounce search - wait 500ms after user stops typing OR deleting before searching
  // This reduces API calls and saves resources for both typing and deletion scenarios
  useEffect(() => {
    // Skip debounce on initial mount when searchTerm is empty (we already fetch on mount)
    if (isInitialMount.current) {
      if (searchTerm === "") {
        isInitialMount.current = false;
        return;
      }
      isInitialMount.current = false;
    }

    // Clear any existing timer (this happens on every searchTerm change - typing or deleting)
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Set up debounce timer - works for both typing and deleting characters
    debounceTimerRef.current = setTimeout(() => {
      if (searchTerm.trim() === "") {
        // If search term is empty after debounce (user deleted all characters), fetch all contacts
        fetchContacts();
      } else {
        // Otherwise, search with the current term (works for both typing and partial deletion)
        searchContacts(searchTerm);
      }
      // Clear the timer reference after execution
      debounceTimerRef.current = null;
    }, 500); // 500ms delay - applies to both typing and deletion to save API resources

    // Cleanup function to clear timer if searchTerm changes before delay completes
    // This ensures we don't make unnecessary API calls when user is actively typing or deleting
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [searchTerm, searchContacts, fetchContacts]);

  const handleRequestFeedback = () => {
    // Handle request feedback action
    console.log("Request feedback for all contacts");
    dispatch({
      type: "SET_STATUS",
      payload: {
        message: "Feedback request sent for all contacts",
        code: "",
        display: "show",
        severity: "success",
      },
    });
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    // Don't call searchContacts here - let the debounce useEffect handle it
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === "Enter") {
      // Cancel any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // On Enter, search immediately without waiting for debounce
      searchContacts(searchTerm);
    }
  };

  return (
    <Grid container spacing={0} justify="center" className={classes.container}>
      <Grid item xs={12} md={10}>
        <Typography variant="h4" className={classes.title}>
          Contacts Feedback
        </Typography>
        {loading ? (
          <Typography>Loading contacts...</Typography>
        ) : (
          <>
            <div className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={handleRequestFeedback}
              >
                Request Feedback
              </Button>
            </div>
            <div className={classes.searchContainer}>
              <TextField
                label="Search by First Name, Last Name, or Membership Number"
                variant="outlined"
                className={classes.searchField}
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                disabled={isSearching}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table className={classes.table} aria-label="contacts table">
                <TableHead>
                  <TableRow>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Membership Number</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No contacts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    contacts.map((contact, index) => (
                      <TableRow key={contact.contactid || contact.id || index}>
                        <TableCell>{contact.firstname || ""}</TableCell>
                        <TableCell>{contact.lastname || ""}</TableCell>
                        <TableCell>{contact.ctpt_membershipnumber || ""}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Grid>
    </Grid>
  );
}

export default ContactsFeedback;

